import { format, isBefore, isToday, parseISO, isValid, getYear, setYear, isSameDay, toDate, differenceInYears, differenceInDays, addDays, addMonths, addYears, isPast, differenceInMinutes } from 'date-fns';

export const getDaysUntil = (date, isBirthday = false) => {
  const today = toDate(new Date());
  if (!date) {
    return -1;
  }
  const parsedDate = parseISO(date);
  if (isBirthday) {
    const age = differenceInYears(today, parsedDate);
    let nextBirthday = addYears(parsedDate, age + 1);
    if (isSameDay(nextBirthday, today)) {
      return 0;
    }
    return differenceInDays(nextBirthday, today);
  }
  return differenceInDays(parsedDate, today);
}

export const getMinutesUntil = (date, isBirthday = false) => {
  const today = toDate(new Date());
  if (!date) {
    return -1;
  }
  const parsedDate = parseISO(date);
  if (isBirthday) {
    const age = differenceInYears(today, parsedDate);
    let nextBirthday = addYears(parsedDate, age + 1);
    if (isSameDay(nextBirthday, today)) {
      return 0;
    }
    return differenceInMinutes(nextBirthday, today);
  }
  return differenceInMinutes(parsedDate, today);
}

export const getAge = (date1, date2 = null) => {
  if (!date1) {
    return -1;
  }
  let parsedDate1 = parseISO(date1);
  let parsedDate2 = parseISO(date2);
  if (isValid(parsedDate1)) {
    if (getYear(parsedDate1) === 1904) {
      return -1;
    }
    let today = toDate(new Date());
    if (parsedDate2 && isValid(parsedDate2)) {
      today = parsedDate2;
    }
    const isBirthdayToday = isSameDay(today, setYear(parsedDate1, getYear(today)));
    let age = differenceInYears(today, parsedDate1);
    return age + (isBirthdayToday ? 0 : 1);
  }
  return -1;
}

export const getStringDate = (date, withTime = false) => {
  if (date) {
    const dateTime = parseISO(date);
    if (isValid(dateTime)) {
      if (withTime) {
        return format(dateTime, 'yyyy-MM-dd hh:mm:ss');
      } else {
        return format(dateTime, 'yyyy-MM-dd');
      }
    }
    return 'Invalid Date';
  }
  return '';
}

export const getPrettyDate = (date, withTime, showYear = true, showSeconds = true) => {
  if (date) {
    let dateTime = parseISO(date);
    if (isValid(dateTime)) {
      if (getYear(dateTime) === 1904 || !showYear) {
        return format(dateTime, 'MMMM do');
      } else if (withTime) {
        if (showSeconds) {
          return format(dateTime, "MMMM do, yyyy 'at' hh:mm:ss aaa");
        }
        return format(dateTime, "MMMM do, yyyy 'at' hh:mm aaa");
      }
      return format(dateTime, 'MMMM do, yyyy');
    }
    return '';
  }
  return '';
}

export const getShortPrettyDate = (date, withTime = false, showYear = true, showSeconds = true) => {
  if (date) {
    const dateTime = parseISO(date);
    if (isValid(dateTime)) {
      if (getYear(dateTime) === 1904 || !showYear) {
        return format(dateTime, 'dd MMM');
      } else if (withTime) {
        if (showSeconds) {
          return format(dateTime, "dd MMM yyyy 'at' hh:mm:ss aaa");
        }
        return format(dateTime, "dd MMM yyyy 'at' hh:mm aaa");
      }
      return format(dateTime, 'dd MMM yyyy');
    }
    return '';
  }
  return '';
}

export const getShortMonthPrettyDate = (date, withTime, showYear = true, showSeconds = true) => {
  if (date) {
    let dateTime = parseISO(date);
    if (isValid(dateTime)) {
      if (getYear(dateTime) === 1904 || !showYear) {
        return format(dateTime, 'MMM do');
      } else if (withTime) {
        if (showSeconds) {
          return format(dateTime, "MMM do, yyyy 'at' hh:mm:ss aaa");
        }
        return format(dateTime, "MMM do, yyyy 'at' hh:mm aaa");
      }
      return format(dateTime, 'MMM do, yyyy');
    }
    return '';
  }
  return '';
}

export const getPrettyTime = (date) => {
  if (date) {
    let dateTime = parseISO(date);
    if (isValid(dateTime)) {
      return format(dateTime, 'hh:mm aaa');
    }
    return '';
  }
  return '';
}

export const minutesToHHmm = (value) => {
  const days = (value / 1440);
  const rDays = Math.floor(days);

  const hours = (value / 60);
  let rhours = Math.floor(hours);
  const minutes = (hours - rhours) * 60;
  let rminutes = Math.round(minutes);
  if (rminutes === 60) {
    rhours += 1;
    rminutes = 0;
  }

  if (value <= 0) {
    return '0 minutes';
  }
  if (rDays > 2) {
    return `${rDays} day${rDays === 1 ? '' : 's'}`;
  }
  const hoursText = rhours > 0 ? `${rhours} hour${rhours === 1 ? '' : 's'}${rminutes > 0 ? ', ' : ''}` : ''
  const minutesText = rminutes > 0 ? `${rminutes} minute${rminutes === 1 ? '' : 's'}` : '';
  return `${hoursText}${minutesText}`;
}

export const getReminderDisplayDateDecorator = (reminder) => {
  let displayDateDecorator = '';
  if (reminder && reminder.calendarDttm) {
    const age = getAge(reminder.calendarDttm);
    if (age > 0) {
      if (reminder.calendarTypeId === 1) {
        displayDateDecorator = ` (age ${age})`;
      } else if ((reminder.calendarTypeId === 2 || reminder.calendarTypeId === 6) || (reminder.calendarTypeId === 4 && reminder.frequencyCd === 'Y')) {
        displayDateDecorator = ` (${age} years)`;
      }
    }
  }
  return displayDateDecorator;
}

export const getReminderDisplayDate = (reminder) => {
  let displayDate = '';
  let displayDateDecorator = getReminderDisplayDateDecorator(reminder);
  if (reminder && reminder.calendarDttm) {
    const dateTime = parseISO(reminder.calendarDttm);
    if (isValid(dateTime)) {
      displayDate = getPrettyDate(reminder.calendarDttm);
    }
  }
  return `${displayDate}${displayDateDecorator}`;
}

export const getDatesArray = (startDate = toDate(new Date()), endDate = addMonths(toDate(new Date()), 3)) => {
  var dates = [];
  let startDateClone = startDate;
  while (isBefore(startDateClone, endDate)) {
    dates.push(startDateClone);
    startDateClone = addDays(startDateClone, 1);
  }
  return dates;
}

export const getCardSendScheduleDateValues = (startDate = toDate(new Date()), endDate = addMonths(toDate(new Date()), 12)) => {
  const dates = getDatesArray(startDate, endDate);
  return dates.map(d => ({ label: isToday(d) ? 'Immediately/Today' : format(d, 'MMMM do, yyyy (EEEE)'), value: format(d, 'yyyy-MM-dd') }));
}

export const getOrdinalSuffix = num => {
  const int = parseInt(num),
    digits = [int % 10, int % 100],
    ordinals = ['st', 'nd', 'rd', 'th'],
    oPattern = [1, 2, 3, 4],
    tPattern = [11, 12, 13, 14, 15, 16, 17, 18, 19];
  return oPattern.includes(digits[0]) && !tPattern.includes(digits[1])
    ? ordinals[digits[0] - 1]
    : ordinals[3];
}
